import { ConnectedWallet, PollTxOptions, UnsignedTx, WalletController, WalletName, WalletType } from "cosmes/wallet"
import React, { useEffect, useState } from "react"
import { createContext, ReactNode, useContext } from "react"
import { CHAIN_ID, MULTIPLIER, WALLET_ID_KEY, WALLET_TYPE_KEY } from "utils/Constants"
import { isMobileDevice } from "utils/Functions"
import { SUPPORTED_WALLETS, WalletSupport } from "utils/WalletSupport"

type Tax = {
    denom: string,
    amount: number
}

type Prop = {
    id?: string,
    address?: string,
    connect: (wallet: WalletSupport) => Promise<void>,
    disconnect: () => void,
    post: (tx: UnsignedTx, taxes?: Tax[]) => Promise<string>,
    pollTx?: (txHash: string) => Promise<any>
}

const WalletContext = createContext<Prop>({
    connect: async () => { },
    disconnect: () => { },
    post: async () => ""
})

export const useWallet = () => useContext(WalletContext)

export function WalletProvider({ children }: { children: ReactNode }) {
    const [wallet, setWallet] = useState<ConnectedWallet>()
    const [controller, setController] = useState<WalletController>()

    const store = (id: string, type: string) => {
        localStorage.setItem(WALLET_ID_KEY, id)
        localStorage.setItem(WALLET_TYPE_KEY, type)
    }

    const loadWalletFromStorage = async () => {
        const id = localStorage.getItem(WALLET_ID_KEY)
        const type = localStorage.getItem(WALLET_TYPE_KEY)

        if (id && type) {
            const supportWallet = new WalletSupport(id as WalletName, type as WalletType)
            setTimeout(async () => await connect(supportWallet), 500)
        }
    }

    const connect = async (walletSupport: WalletSupport) => {
        try {
            const wallet = await walletSupport.connect();
            setWallet(wallet)
            store(wallet!.id, wallet!.type)
        } catch (e) {
            alert(`error, reason ${e}`)
        }
    }

    const disconnect = () => {
        localStorage.clear()
        controller?.disconnect([CHAIN_ID])
        setWallet(undefined)
        setController(undefined)
    }

    const post = async (tx: UnsignedTx, taxes?: Tax[]) => {
        let fee = await wallet!.estimateFee(tx, MULTIPLIER)

        if (taxes) fee.amount = fee.amount.map(coin => {
            const tax = taxes.find(e => e.denom == coin.denom)
            if (tax) coin.amount = (Number(coin.amount) + tax.amount).toString()

            return coin
        })

        return await wallet!.broadcastTx(tx, fee)
    }

    useEffect(() => {
        for (const controller of Object.values(SUPPORTED_WALLETS).map(e => e.controller)) {
            controller.onDisconnect(_ => disconnect())
            controller.onAccountChange(_ => disconnect())
        }

        loadWalletFromStorage()
    }, [])

    return <WalletContext.Provider
        children={children}
        value={{
            pollTx: wallet?.pollTx ? async (txHash) => wallet.pollTx(txHash, { intervalSeconds: 1, maxAttempts: 20 }) : undefined,
            post,
            connect,
            disconnect,
            address: wallet?.address,
            id: wallet?.id
        }}
    />
}